<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-button @click.native="onShowModal(null)">New item</app-button>
        <app-input
          @input.native="onSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by number"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="category_filter"
          :options="category_options"
          :reduce="item => item.id"
          placeholder="Category"
          class="select--200"
        />
        <app-select
          @input="onChangeFilter"
          v-model="brand_filter"
          :options="brand_options"
          :reduce="item => item.id"
          placeholder="Brand"
          class="select--200"
        />
        <app-select
          @input="onChangeFilter"
          v-model="size_filter"
          :options="size_options"
          :reduce="item => item.id"
          placeholder="Size"
          class="select--200"
        />
      </app-cells>
      <app-cells><h2 class="title">Pending stocktaking</h2></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__item><div class="table-default__left">Item</div></template>
        <template #item="{ row }">
          <div class="table-default__left">
            <button class="table-default__link-border" @click="onShowModal(row)">
              {{ row.number }} - {{ row.category ? `${row.category.name} | ` : '' }}{{ row.brand ? `${row.brand.name} | ` : '' }}{{ row.color ? `${row.color.name} | ` : '' }}{{ row.size ? `${row.size.name}` : '' }}
            </button>
          </div>
        </template>
        <template #condition="{ row }">
          <div class="conditions">
            <span
              v-for="item of 5"
              :key="item"
              class="conditions__item"
              :class="{'conditions__item--checked': item <= row.condition}"
            />
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
      <app-cells><h2 class="title">Confirmed</h2></app-cells>
      <v-client-table
        :data="table_confirmed.items"
        :columns="table_confirmed.columns"
        :options="table_confirmed.options"
        class="table-default"
        :class="{ 'table-default--loading': table_confirmed_preloader }"
      >
        <template #h__item><div class="table-default__left">Item</div></template>
        <template #item="{ row }">
          <div class="table-default__left">
            <router-link :to="{name: 'rental-items-detail', params: {id: row.id }}" class="table-default__link-border">
              {{ row.number }} - {{ row.category ? `${row.category.name} | ` : '' }}{{ row.brand ? `${row.brand.name} | ` : '' }}{{ row.color ? `${row.color.name} | ` : '' }}{{ row.size ? `${row.size.name}` : '' }}
            </router-link>
          </div>
        </template>
        <template #condition="{ row }">
          <div class="conditions">
            <span
              v-for="item of 5"
              :key="item"
              class="conditions__item"
              :class="{'conditions__item--checked': item <= row.condition}"
            />
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationConfirmed"
            v-if="pagination_confirmed.pages > 1"
            v-model="pagination_confirmed.page"
            :page-count="pagination_confirmed.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_confirmed.count" :count="table_confirmed.items.length" :page="pagination_confirmed.page" />
      </app-cells>
    </template>
    <modal
      name="rental-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('rental-form')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ item_to_action ? 'Verify item' : 'Adding new item' }}</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Number" required label-for="form_id">
              <app-input
                v-model.number="form.number"
                id="form_id"
                type="number"
                min="0"
                placeholder="Type"
                name="form_id"
                :error="$v.form.number.$error"
              />
              <template #error>
                <div v-if="$v.form.number.$dirty && !$v.form.number.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Category" required>
              <app-select
                v-model="form.category"
                :options="category_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Choose"
                :error="$v.form.category.$error"
              />
              <template #error>
                <div v-if="$v.form.category.$dirty && !$v.form.category.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Brand" required>
              <app-select
                v-model="form.brand"
                :options="brand_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Choose"
                :error="$v.form.brand.$error"
              />
              <template #error>
                <div v-if="$v.form.brand.$dirty && !$v.form.brand.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Color" required>
              <app-select
                v-model="form.color"
                :options="color_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Choose"
                :error="$v.form.color.$error"
              />
              <template #error>
                <div v-if="$v.form.color.$dirty && !$v.form.color.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Size" required>
              <app-select
                v-model="form.size"
                :options="size_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Choose"
                :error="$v.form.size.$error"
              />
              <template #error>
                <div v-if="$v.form.size.$dirty && !$v.form.size.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Status" required v-if="item_to_action">
              <app-select
                v-model="form.status"
                :options="status_options"
                :reduce="item => item.code"
                :clearable="false"
                placeholder="Choose"
                :error="$v.form.status.$error"
              />
              <template #error>
                <div v-if="$v.form.status.$dirty && !$v.form.status.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Condition" required>
              <div class="conditions conditions--form">
                <label
                  v-for="item of 5"
                  :key="item"
                  class="conditions__item"
                  :class="{'conditions__item--checked': item <= form.condition}"
                >
                  <input v-model="form.condition" :value="item" type="radio">
                </label>
              </div>
              <template #error>
                <div v-if="$v.form.condition.$dirty && !$v.form.condition.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">{{ item_to_action ? 'Verify' : 'Add item' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import {
  getEquipmentStorageBrand,
  getEquipmentStorageCategory,
  getEquipmentStorageColor,
  getEquipmentStorageItems,
  getEquipmentStorageSize,
  getRentalItemsStatus,
  patchEquipmentStorageItem,
  postEquipmentStorageItem,
} from '@/http'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'RentalItemsActive',
  data() {
    return {
      is_loaded: false,
      item_to_action: null,
      search: '',
      category_filter: '',
      brand_filter: '',
      size_filter: '',
      table: {
        items: [],
        columns: ['item', 'condition', 'controls'],
        options: {
          headings: {
            item: 'Client',
            condition: 'Condition',
            controls: '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_confirmed: {
        items: [],
        columns: ['item', 'condition'],
        options: {
          headings: {
            item: 'Client',
            condition: 'Condition',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_confirmed: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form: {},
      category_options: [],
      brand_options: [],
      color_options: [],
      size_options: [],
      status_options: [],
      table_preloader: false,
      table_confirmed_preloader: false,
    }
  },
  validations: {
    form: {
      category: { required },
      brand: { required },
      color: { required },
      size: { required },
      number: { required },
      condition: { required },
      status: {
        required: requiredIf(function() {
          return this.item_to_action
        })
      },
    }
  },
  created() {
    this.fetchTables()
    getEquipmentStorageCategory().then(response => { this.category_options = response.data })
    getEquipmentStorageBrand().then(response => { this.brand_options = response.data })
    getEquipmentStorageColor().then(response => { this.color_options = response.data })
    getEquipmentStorageSize().then(response => { this.size_options = response.data })
    getRentalItemsStatus().then(response => { this.status_options = response.data })
  },
  methods: {
    fetchTables() {
      this.fetchItems()
      this.fetchItemsConfirmed()
    },
    fetchItems() {
      this.table_preloader = true
      getEquipmentStorageItems({
        page: this.pagination.page,
        page_size: 20,
        status: 'active',
        number: this.search,
        category: this.search ? '' : this.category_filter,
        brand: this.search ? '' : this.brand_filter,
        size: this.search ? '' : this.size_filter,
        verified: false,
      }).then(res => {
        this.table.items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    fetchItemsConfirmed() {
      this.table_confirmed_preloader = true
      getEquipmentStorageItems({
        page: this.pagination_confirmed.page,
        page_size: 20,
        status: 'active',
        number: this.search,
        category: this.search ? '' : this.category_filter,
        brand: this.search ? '' : this.brand_filter,
        size: this.search ? '' : this.size_filter,
        verified: true,
      }).then(res => {
        this.table_confirmed.items = res.data.results
        this.pagination_confirmed.pages = res.data.pages
        this.pagination_confirmed.count = res.data.count
        this.table_confirmed_preloader = false
      })
    },
    onShowModal(item) {
      this.item_to_action = item?.id
      if (this.item_to_action) {
        this.form = {
          category: item.category.id,
          brand: item.brand.id,
          color: item.color.id,
          size: item.size.id,
          status: item.status.code,
          number: item.number,
          condition: item.condition,
          verified: true,
        }
      } else {
        this.form = {}
      }
      this.$v.form.$reset()
      this.$modal.show('rental-form')
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.pagination_confirmed.page = 1
      this.fetchTables()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.item_to_action) {
        patchEquipmentStorageItem(this.item_to_action, this.form)
          .then(() => {
            this.fetchTables()
            this.$notify({
              type: 'success',
              title: 'Item verified',
            })
            this.$modal.hide('rental-form')
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        postEquipmentStorageItem(this.form)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Item added',
            })
            this.$modal.hide('rental-form')
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onClickPaginationConfirmed(page) {
      this.pagination.page = page
      this.fetchItemsConfirmed()
    },
    onSearch() {
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchTables()
    }, 350),
  }
}
</script>

